/**
 * Pfad-Tenant (TQ #409, SPEC-Cockpit-pro-Kunde §4.1 v1.5 / E9).
 *
 * Kunden-Instanzen laufen unter `https://cockpit.it-sce.de/<kennung>/`. Die Kennung ist das
 * ERSTE Pfadsegment (10 Zeichen, Alphabet ohne i/l/o/0/1). Der Ingress schneidet `/<kennung>`
 * ab (StripPrefix), nginx der Instanz setzt `<base href="/<kennung>/">` (Env COCKPIT_BASE_PATH),
 * damit die relativen Assets (Vite `base: './'`) unter dem Instanz-Pfad geladen werden.
 *
 * Kunden-Instanz-Modus gilt nur, wenn BEIDE Seiten uebereinstimmen:
 *   - Browser-Pfad beginnt mit `/<kennung>` (Kennungs-Regex), und
 *   - `<base href>` der ausgelieferten index.html ist genau `/<kennung>/`.
 * `<base href="/">` (Image-Default) = Legacy-Cockpit, auch wenn ein Legacy-Slug zufaellig wie
 * eine Kennung aussieht. Base gesetzt, Pfad passt nicht = Fehler (fail-closed).
 * Kein Inline-Script noetig (CSP): die Base kommt aus nginx, nicht aus JS.
 */

export const KENNUNG_RE = /^[a-hj-km-np-z][a-hj-km-np-z2-9]{9}$/

export type PathTenant =
  | { kind: 'legacy' }
  | { kind: 'instance'; id: string; basePath: string }
  | { kind: 'error'; reason: string }

/** Erstes Pfadsegment, wenn es eine gueltige Kennung ist; sonst null. */
export function kennungFromPath(pathname: string): string | null {
  const seg = pathname.split('/')[1] ?? ''
  return KENNUNG_RE.test(seg) ? seg : null
}

/** Rein: Pfad + `<base href>`-Attribut -> Modus. */
export function detectPathTenant(pathname: string, baseHref: string | null | undefined): PathTenant {
  const base = (baseHref ?? '').trim()
  if (base === '' || base === '/') return { kind: 'legacy' }

  const m = /^\/([^/]+)\/$/.exec(base)
  if (!m || !KENNUNG_RE.test(m[1])) {
    return { kind: 'error', reason: `ungueltiges <base href> "${base}" (erwartet "/" oder "/<kennung>/")` }
  }
  const id = m[1]
  if (kennungFromPath(pathname) !== id) {
    return { kind: 'error', reason: `Pfad passt nicht zur Instanz (erwartet /${id}/…)` }
  }
  return { kind: 'instance', id, basePath: `/${id}` }
}

let cached: PathTenant | null = null

/** Modus der laufenden Seite (einmal ermittelt, Pfad-Wechsel per Router aendern ihn nicht). */
export function currentPathTenant(): PathTenant {
  if (!cached) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      cached = { kind: 'legacy' }
    } else {
      const baseHref = document.querySelector('base')?.getAttribute('href')
      cached = detectPathTenant(window.location.pathname, baseHref)
    }
  }
  return cached
}

/** Nur fuer Tests. */
export function setPathTenantForTest(t: PathTenant | null): void {
  cached = t
}

/** Router-Basename: `/<kennung>` im Instanz-Modus, sonst undefined (Legacy unveraendert). */
export function routerBasename(t: PathTenant = currentPathTenant()): string | undefined {
  return t.kind === 'instance' ? t.basePath : undefined
}

/** Pfad der Laufzeit-Config: `/<kennung>/config.json` im Instanz-Modus, sonst `/config.json`. */
export function configJsonPath(t: PathTenant = currentPathTenant()): string {
  return t.kind === 'instance' ? `${t.basePath}/config.json` : '/config.json'
}
